import { useEffect, useRef, useState } from 'react';

/** The part of the CloseWatcher API (Chromium 126+, Android's back button and gesture) used here. */
interface CloseWatcherLike {
  onclose: ((event: Event) => void) | null;
  destroy(): void;
}
type CloseWatcherClass = new () => CloseWatcherLike;

const closeWatcher = (): CloseWatcherClass | undefined =>
  typeof window === 'undefined' ? undefined : (window as unknown as { CloseWatcher?: CloseWatcherClass }).CloseWatcher;

/** Opened from the home screen as an installed app, where the back button at the first screen closes it. */
export function installedApp(): boolean {
  if (typeof window === 'undefined') return false;
  return ['standalone', 'fullscreen', 'minimal-ui'].some((mode) => window.matchMedia?.(`(display-mode: ${mode})`).matches === true);
}

/**
 * While `enabled`, the back button (and on a keyboard Escape that nothing else took) runs `onBack` instead of
 * leaving the page. One watcher at a time, made again after each press: the browser lets a page keep one
 * without a tap in between, so the back button never gets stuck. Without CloseWatcher it does nothing.
 */
export function useBackButton(enabled: boolean, onBack: () => void) {
  const handler = useRef(onBack);
  useEffect(() => {
    handler.current = onBack;
  });
  const [presses, setPresses] = useState(0);
  useEffect(() => {
    const Watcher = closeWatcher();
    if (!enabled || !Watcher) return undefined;
    const watcher = new Watcher();
    watcher.onclose = () => {
      handler.current();
      setPresses((n) => n + 1);
    };
    return () => watcher.destroy();
  }, [enabled, presses]);
}
